/**
 * ============================================================================
 * ESCROW STATUS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Displays escrow status with visual indicators
 */

import React from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import StatusBadge from '../common/StatusBadge';
import ProgressBar from '../common/ProgressBar';

/**
 * Escrow Status Component
 * @param {Object} props
 * @param {Object} props.escrow - Escrow object
 * @returns {JSX.Element}
 */
const EscrowStatus = ({ escrow }) => {
  const { formatCurrency, language } = useI18n();

  if (!escrow) return null;

  const getStatusVariant = (status) => {
    const variants = {
      pending: 'warning',
      funded: 'success',
      frozen: 'error',
      released: 'success',
      refunded: 'info',
      partial: 'info',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      funded: '🔒',
      frozen: '❄️',
      released: '✅',
      refunded: '↩️',
      partial: '📊',
      cancelled: '❌',
    };
    return icons[status] || '📦';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'from-yellow-500 to-orange-500',
      funded: 'from-blue-500 to-cyan-500',
      frozen: 'from-red-500 to-pink-500',
      released: 'from-green-500 to-emerald-500',
      refunded: 'from-purple-500 to-pink-500',
      partial: 'from-indigo-500 to-purple-500',
      cancelled: 'from-gray-500 to-slate-500',
    };
    return colors[status] || 'from-gray-500 to-slate-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: language === 'fr' ? 'En attente de paiement' : 'Pending Payment',
      funded: language === 'fr' ? 'Fonds verrouillés' : 'Funds Locked',
      frozen: language === 'fr' ? 'Gelé (litige)' : 'Frozen (Dispute)',
      released: language === 'fr' ? 'Libéré au vendeur' : 'Released to Seller',
      refunded: language === 'fr' ? 'Remboursé à l\'acheteur' : 'Refunded to Buyer',
      partial: language === 'fr' ? 'Libération partielle' : 'Partial Release',
      cancelled: language === 'fr' ? 'Annulé' : 'Cancelled',
    };
    return labels[status] || status;
  };

  const getProgressValue = () => {
    const progress = {
      pending: 0,
      funded: 50,
      frozen: 50,
      released: 100,
      refunded: 100,
      partial: 75,
      cancelled: 0,
    };
    return progress[escrow.status] || 0;
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-6">
        {/* Status Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getStatusColor(
                escrow.status
              )} flex items-center justify-center text-3xl shadow-lg`}
            >
              {getStatusIcon(escrow.status)}
            </div>
            <div>
              <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">
                {language === 'fr' ? 'Statut de l\'escrow' : 'Escrow Status'}
              </h3>
              <StatusBadge variant={getStatusVariant(escrow.status)} size="lg">
                {getStatusLabel(escrow.status)}
              </StatusBadge>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <ProgressBar
            value={getProgressValue()}
            variant={
              escrow.status === 'released' || escrow.status === 'refunded'
                ? 'success'
                : escrow.status === 'frozen'
                ? 'error'
                : escrow.status === 'funded'
                ? 'info'
                : 'warning'
            }
            animated
            showLabel
            label={`${getProgressValue()}% ${language === 'fr' ? 'complété' : 'complete'}`}
          />
        </div>

        {/* Amount Display */}
        <Card variant="outlined" className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 border-blue-200 dark:border-blue-800">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {language === 'fr' ? 'Montant en escrow' : 'Escrow Amount'}
            </p>
            <p className="text-3xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
              {formatCurrency(escrow.amount || 0, escrow.currency || 'XAF')}
            </p>
          </div>
        </Card>

        {/* Status Details */}
        <div className="grid grid-cols-2 gap-4">
          {escrow.fundedAt && (
            <Card variant="outlined" className="p-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Financé le' : 'Funded On'}
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {new Date(escrow.fundedAt).toLocaleDateString()}
              </p>
            </Card>
          )}
          {escrow.autoReleaseAt && (
            <Card variant="outlined" className="p-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Libération auto' : 'Auto-Release'}
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {new Date(escrow.autoReleaseAt).toLocaleDateString()}
              </p>
            </Card>
          )}
          {escrow.releasedAt && (
            <Card variant="outlined" className="p-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Libéré le' : 'Released On'}
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {new Date(escrow.releasedAt).toLocaleDateString()}
              </p>
            </Card>
          )}
          {escrow.frozenAt && (
            <Card variant="outlined" className="p-3">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {language === 'fr' ? 'Gelé le' : 'Frozen On'}
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {new Date(escrow.frozenAt).toLocaleDateString()}
              </p>
            </Card>
          )}
        </div>

        {/* Release/Refund Amounts */}
        {(escrow.releaseAmount || escrow.refundAmount) && (
          <div className="space-y-2">
            {escrow.releaseAmount > 0 && (
              <div className="flex justify-between items-center p-3 rounded-xl bg-green-50 dark:bg-green-900/20">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {language === 'fr' ? 'Montant libéré' : 'Released Amount'}:
                </span>
                <span className="text-lg font-black text-green-600 dark:text-green-400">
                  {formatCurrency(escrow.releaseAmount, escrow.currency || 'XAF')}
                </span>
              </div>
            )}
            {escrow.refundAmount > 0 && (
              <div className="flex justify-between items-center p-3 rounded-xl bg-purple-50 dark:bg-purple-900/20">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {language === 'fr' ? 'Montant remboursé' : 'Refunded Amount'}:
                </span>
                <span className="text-lg font-black text-purple-600 dark:text-purple-400">
                  {formatCurrency(escrow.refundAmount, escrow.currency || 'XAF')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default EscrowStatus;
